import { type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../../lib/utils';

import { alertIndicatorDotVariants, alertIndicatorVariants } from './variants';

/**
 * Priority levels for alert indicators
 * - high: Red (urgent medical events)
 * - medium: Amber/Yellow (requires attention)
 * - low: Green (low priority/positive)
 */
export type AlertPriority = 'high' | 'medium' | 'low';

export interface AlertIndicatorProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    Omit<VariantProps<typeof alertIndicatorVariants>, 'priority'> {
  /**
   * Priority level of the alert - determines the color scheme
   */
  priority: AlertPriority;
  /**
   * Alert label text to display
   * If not provided, will show count format
   */
  label?: string;
  /**
   * Number of additional alerts (for grouped display)
   * Only shown when label is not provided
   * @example count=3 renders "+3 more"
   */
  count?: number;
  /**
   * Priorities to render as dots, in order, when a grouped count spans
   * multiple priorities. Overrides the single `priority` dot.
   * @example dotPriorities={['high', 'medium']} count={4} renders a red and
   * an amber dot side by side followed by "+4 more"
   */
  dotPriorities?: AlertPriority[];
  /**
   * Whether to show the indicator dot
   * @default true
   */
  showDot?: boolean;
  /**
   * Ellipsize the text when the parent constrains the width instead of
   * letting it wrap or be clipped abruptly. The full text stays available
   * via the title tooltip.
   * @default false
   */
  truncate?: boolean;
  /**
   * Custom class for the indicator dot
   */
  dotClassName?: string;
  /**
   * Custom styles for the indicator dot
   */
  dotStyle?: React.CSSProperties;
}

/**
 * AlertIndicator - A flexible component for displaying medical alerts and notifications
 *
 * @example
 * // With label
 * <AlertIndicator priority="high" label="Episode of AT/AF" />
 *
 * @example
 * // With count (shows as black text)
 * <AlertIndicator priority="medium" count={3} /> // Shows "+3 more"
 *
 * @example
 * // Multiple alerts
 * <div className="flex gap-2">
 *   <AlertIndicator priority="high" label="1 episode of VF" />
 *   <AlertIndicator priority="medium" count={3} />
 * </div>
 */
export const AlertIndicator = React.forwardRef<
  HTMLDivElement,
  AlertIndicatorProps
>(function AlertIndicator(
  {
    priority,
    label,
    count,
    dotPriorities,
    showDot = true,
    truncate = false,
    dotClassName,
    dotStyle,
    className,
    children,
    title,
    ...props
  },
  ref,
) {
  // Determine what to display
  const displayText = React.useMemo(() => {
    // If children provided, use that
    if (children) return children;
    // If label provided, use that
    if (label) return label;
    // If count provided, format as "+N more"
    if (count !== undefined && count > 0) {
      return `+${count} more`;
    }
    // Fallback to empty
    return null;
  }, [children, label, count]);

  // When showing count, text should be black instead of colored
  const isCountMode = !children && !label && count !== undefined && count > 0;

  return (
    <div
      ref={ref}
      className={cn(
        alertIndicatorVariants({
          priority: isCountMode ? undefined : priority,
        }),
        isCountMode && 'text-gray-900',
        truncate && 'min-w-0 max-w-full',
        className,
      )}
      role="status"
      aria-label={
        label ||
        (count ? `${count} additional alert${count !== 1 ? 's' : ''}` : 'Alert')
      }
      title={
        title ??
        (truncate && typeof displayText === 'string' ? displayText : undefined)
      }
      {...props}
    >
      {showDot &&
        (dotPriorities?.length ? dotPriorities : [priority]).map(
          (dotPriority) => (
            <span
              key={dotPriority}
              aria-hidden="true"
              className={cn(
                alertIndicatorDotVariants({ priority: dotPriority }),
                dotClassName,
              )}
              style={dotStyle}
            />
          ),
        )}
      {displayText && (
        <span className={cn(truncate && 'min-w-0 truncate')}>
          {displayText}
        </span>
      )}
    </div>
  );
});
